
import { defineComponent, onMounted, ref, watch, nextTick, Ref } from 'vue'
import ContentLayout from '@/views/layout/ContentLayout.vue'
import LoadingButton from '@/views/components/LoadingButton.vue'
import { download, isBlob } from '@/views/utils'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { hasMessage, isAxiosError } from '@/repositories/errorHandler'
import { apiErrorMessage } from '@/apiErrorMessage'
import { callApiWrapper } from '@/views/callApiWrapper'
import { keieiJohoRepositories } from '@/repositories/keiei-joho/keieiJohoRepositories'
import ResponseResultAlert from '@/views/components/ResponseResultAlert.vue'
import { initializeTaishakuTaishoKmk, ResponseTaishakuTaishoExportLayout, TaishakuTaishoKmkType } from '@/repositories/keiei-joho/taishaku-taisho/data'
import {
  OutputFundTypeType,
  outputFundTypeType,
  taishakuTaishoCategoryTypeType as CategoryType,
  taishakuTaishoElementTypeType as ElementType,
  taishakuTaishoKmkTypeType as KmkType,
  taishakuTaishoSubcategoryTypeType as SubcategoryType
} from '@/repositories/keiei-joho/data'
import { corporationTypeType, CorporationTypeType } from '@/views/corporation-info/types'
import TaishakuTaishoExportReportShisan from '@/views/keiei-joho/taishaku-taisho/TaishakuTaishoExportReportShisan.vue'
import TaishakuTaishoExportReportFusai from '@/views/keiei-joho/taishaku-taisho/TaishakuTaishoExportReportFusai.vue'
import TaishakuTaishoExportReportJunshisan from '@/views/keiei-joho/taishaku-taisho/TaishakuTaishoExportReportJunshisan.vue'
import InputKin from '@/views/components/InputKin.vue'

const repository = keieiJohoRepositories.taishakuTaishoExport

// element、category、subcategoryに一致する配列を返す
function getItemsBy (types: TaishakuTaishoKmkType[], eType: string, cType?: string, sType?: string) {
  return types.filter(v =>
    v.elementType === eType && (cType ? v.categoryType === cType : true) && (sType ? v.subcategoryType === sType : true)
  )
}

// kmkTypeに一致するのを返す
function getItemsByKmkType (types: TaishakuTaishoKmkType[], kType: string) {
  return types.find(v => v.kmkType === kType)
}

// 入力チェック
function inputCheck (
  corporationType: CorporationTypeType,
  allItems: TaishakuTaishoKmkType[],
  requestError: Ref<string | null>
) {
  const ryudoShisan = getItemsByKmkType(allItems, KmkType.RyudoShisan)?.kin
  if (ryudoShisan === null) {
    requestError.value = '流動資産は必須です。'
    return false
  }

  const yukeiKotei = getItemsByKmkType(allItems, KmkType.YukeiKoteiShisan)?.kin
  if (yukeiKotei === null) {
    requestError.value = '有形固定資産は必須です。'
    return false
  }

  const mukeiKotei = getItemsByKmkType(allItems, KmkType.MukeiKoteiShisan)?.kin
  if (mukeiKotei === null) {
    requestError.value = '無形固定資産は必須です。'
    return false
  }

  const otherShisan = getItemsByKmkType(allItems, KmkType.OtherShisan)?.kin
  if (otherShisan === null) {
    requestError.value = 'その他の資産は必須です。'
    return false
  }

  const ryudoFusai = getItemsByKmkType(allItems, KmkType.RyudoFusai)?.kin
  if (ryudoFusai === null) {
    requestError.value = '流動負債は必須です。'
    return false
  }

  const koteiFusai = getItemsByKmkType(allItems, KmkType.KoteiFusai)?.kin
  if (koteiFusai === null) {
    requestError.value = '固定負債は必須です。'
    return false
  }

  const junshisanGokei = getItemsByKmkType(allItems, KmkType.JunshisanGokei)?.kin
  if (junshisanGokei === null) {
    requestError.value = '純資産合計は必須です。'
    return false
  }

  if (corporationType === corporationTypeType.Type1) {
    const otherShisan = getItemsByKmkType(allItems, KmkType.OtherShisan)?.kin ?? 0
    const hoyuIryo = getItemsByKmkType(allItems, KmkType.HoyuIryoKikanSai)?.kin ?? 0
    if (otherShisan < hoyuIryo) {
      requestError.value = '保有医療機関債 は その他の資産 以下になるよう入力してください。'
      return false
    }

    const koteiFusai = getItemsByKmkType(allItems, KmkType.KoteiFusai)?.kin ?? 0
    const iryoKikan = getItemsByKmkType(allItems, KmkType.IryoKikanSai)?.kin ?? 0
    if (koteiFusai < iryoKikan) {
      requestError.value = '医療機関債 は 固定負債 以下になるよう入力してください。'
      return false
    }

    const tsumitate = getItemsByKmkType(allItems, KmkType.TsumitateKin)?.kin ?? 0
    const daitai = getItemsByKmkType(allItems, KmkType.DaitaiKikin)?.kin ?? 0
    if (tsumitate < daitai) {
      requestError.value = '代替基金 は 積立金 以下になるよう入力してください。'
      return false
    }
  }

  return true
}

export default defineComponent({
  components: {
    InputKin,
    TaishakuTaishoExportReportJunshisan,
    TaishakuTaishoExportReportFusai,
    TaishakuTaishoExportReportShisan,
    ResponseResultAlert,
    LoadingButton,
    ContentLayout
  },
  setup () {
    const loading = ref(false)
    const isSucceedRequest = ref(false)
    const requestError = ref<string | null>(null)
    const hasError = ref<boolean>(false)
    const store = useStore()
    const router = useRouter()

    const accountingEndDate = ref('')
    accountingEndDate.value = store.state.accountingEndDate

    // 集約する科目ごとに配列保持
    //  資産：流動資産、有形固定資産、無形固定資産、その他の資産
    //  負債：流動負債、固定負債
    //  純資産：積立金、評価・換算差額等
    const ryudoShisanItems = ref<TaishakuTaishoKmkType[]>([])
    const yukeiKoteiShisanItems = ref<TaishakuTaishoKmkType[]>([])
    const mukeiKoteiShisanItems = ref<TaishakuTaishoKmkType[]>([])
    const otherShisanItems = ref<TaishakuTaishoKmkType[]>([])
    const ryudoFusaiItems = ref<TaishakuTaishoKmkType[]>([])
    const koteiFusaiItems = ref<TaishakuTaishoKmkType[]>([])
    const tsumitateKinItems = ref<TaishakuTaishoKmkType[]>([])
    const hyokaSagakuItems = ref<TaishakuTaishoKmkType[]>([])

    // 資産合計、固定資産合計、負債合計、純資産合計、負債・純資産合計
    // 各合計値は子コンポーネント側で自動計算するようにした
    const shisanGokei = ref<TaishakuTaishoKmkType>(initializeTaishakuTaishoKmk())
    const koteiShisanGokei = ref<TaishakuTaishoKmkType>(initializeTaishakuTaishoKmk())
    const fusaiGokei = ref<TaishakuTaishoKmkType>(initializeTaishakuTaishoKmk())
    const junshisanGokei = ref<TaishakuTaishoKmkType>(initializeTaishakuTaishoKmk())
    const fusaiJunshisanGokei = ref<TaishakuTaishoKmkType>(initializeTaishakuTaishoKmk())

    // 出資金・基金
    const shushikin = ref<TaishakuTaishoKmkType>(initializeTaishakuTaishoKmk())
    const kikin = ref<TaishakuTaishoKmkType>(initializeTaishakuTaishoKmk())

    // 出資金か基金か
    const outputFundType = ref<OutputFundTypeType>(outputFundTypeType.Shushikin)

    // 法人情報
    const corporationName = ref('')
    const location = ref('')
    const medicalCorporationNumber = ref('')
    const corporationType = ref<CorporationTypeType>(corporationTypeType.Type1)

    // 負債・純資産合計の自動計算
    watch([() => fusaiGokei.value.kin, () => junshisanGokei.value.kin], ([fusai, junshisan]) => {
      fusaiJunshisanGokei.value.kin = (fusai ?? 0) + (junshisan ?? 0)
    })

    // 初期値設定
    onMounted(async () => {
      loading.value = true
      await callApiWrapper(requestError, async () => {
        const hasCorporationInfo = await repository.checkCorporationInfoExists()
        if (!hasCorporationInfo) {
          requestError.value = '法人情報が登録されていません。法人情報画面から登録してください。'
          loading.value = false
          hasError.value = true
          return
        }

        const data = await repository.getKmkTypes()
        if (data.corporationInfo.corporationType === corporationTypeType.Type6) {
          window.alert('法人情報の法人タイプに「地域医療連携推進法人」が選択されているため、本機能はご利用できません。')
          await router.push('/top')
        }
        refreshUi(data)
      })
      loading.value = false
    })

    /**
     * レスポンス結果の非表示
     */
    const onDismissAlert = () => {
      isSucceedRequest.value = false
      requestError.value = null
    }

    /**
     * 貸借対照表科目タイプと法人情報のデータ取得
     */
    const refreshUi = (data: ResponseTaishakuTaishoExportLayout) => {
      ryudoShisanItems.value = getItemsBy(data.kmkTypes, ElementType.Shisan, CategoryType.RyudoShisan)
      yukeiKoteiShisanItems.value = getItemsBy(data.kmkTypes, ElementType.Shisan, CategoryType.KoteiShisan, SubcategoryType.YukeiKoteiShisan)
      mukeiKoteiShisanItems.value = getItemsBy(data.kmkTypes, ElementType.Shisan, CategoryType.KoteiShisan, SubcategoryType.MukeiKoteiShisan)
      otherShisanItems.value = getItemsBy(data.kmkTypes, ElementType.Shisan, CategoryType.KoteiShisan, SubcategoryType.OtherShisan)
      ryudoFusaiItems.value = getItemsBy(data.kmkTypes, ElementType.Fusai, CategoryType.RyudoFusai)
      koteiFusaiItems.value = getItemsBy(data.kmkTypes, ElementType.Fusai, CategoryType.KoteiFusai)
      tsumitateKinItems.value = getItemsBy(data.kmkTypes, ElementType.Junshisan, CategoryType.TsumitateKin)
      hyokaSagakuItems.value = getItemsBy(data.kmkTypes, ElementType.Junshisan, CategoryType.HyokaSagaku)

      shisanGokei.value = getItemsByKmkType(data.kmkTypes, KmkType.ShisanGokei)!
      koteiShisanGokei.value = getItemsByKmkType(data.kmkTypes, KmkType.KoteiShisanGokei)!
      fusaiGokei.value = getItemsByKmkType(data.kmkTypes, KmkType.FusaiGokei)!
      junshisanGokei.value = getItemsByKmkType(data.kmkTypes, KmkType.JunshisanGokei)!
      fusaiJunshisanGokei.value = getItemsByKmkType(data.kmkTypes, KmkType.FusaiJunshisanGokei)!

      shushikin.value = getItemsByKmkType(data.kmkTypes, KmkType.Shushikin)!
      kikin.value = getItemsByKmkType(data.kmkTypes, KmkType.Kikin)!

      corporationName.value = data.corporationInfo.corporationName
      location.value = data.corporationInfo.location
      medicalCorporationNumber.value = data.corporationInfo.medicalCorporationNumber
      corporationType.value = data.corporationInfo.corporationType
      outputFundType.value = data.outputFundType
    }

    /**
     * 登録ボタン
     */
    const onClickedRegister = async () => {
      onDismissAlert()
      const allKmkTypes: TaishakuTaishoKmkType[] = [
        ...ryudoShisanItems.value,
        ...yukeiKoteiShisanItems.value,
        ...mukeiKoteiShisanItems.value,
        ...otherShisanItems.value,
        ...ryudoFusaiItems.value,
        ...koteiFusaiItems.value,
        ...tsumitateKinItems.value,
        ...hyokaSagakuItems.value,
        shisanGokei.value,
        koteiShisanGokei.value,
        fusaiGokei.value,
        junshisanGokei.value,
        fusaiJunshisanGokei.value,
        shushikin.value,
        kikin.value
      ]
      if (!inputCheck(corporationType.value, allKmkTypes, requestError)) return

      loading.value = true
      await callApiWrapper(requestError, async () => {
        await repository.save(allKmkTypes)
        isSucceedRequest.value = true
      })
      loading.value = false
    }

    /**
     * 再集計ボタン
     */
    const onClickedRecalculate = async () => {
      onDismissAlert()
      loading.value = true
      await callApiWrapper(requestError, async () => {
        const data = await repository.recalculate()
        refreshUi(data)
      })
      loading.value = false
    }

    /**
     * 出力ボタン
     */
    const onClickedExport = async () => {
      const result = window.confirm('編集内容を登録してからCSVを出力しますか？\n（OK: 登録して出力、キャンセル: 出力のみ）')
      if (result) {
        await onClickedRegister()
        if (requestError.value !== null) return
      }

      onDismissAlert()
      loading.value = true
      try {
        const response = await repository.export()
        download(response)
      } catch (e) {
        if (isAxiosError(e)) {
          if (e.response && e.response.status >= 400 && e.response.headers['content-type'] === 'application/json') {
            if (isBlob(e.response.data)) {
              const json = await e.response.data.text()
              const jsonObj = JSON.parse(json)
              if (hasMessage(jsonObj)) {
                requestError.value = jsonObj.message
              }
            }
          }
        }
        if (requestError.value === null) {
          requestError.value = apiErrorMessage.badRequest
        }
      } finally {
        loading.value = false
      }
    }

    /**
     * 科目項目の入力可否を法人タイプに応じて判定する
     */
    const isDisabledByCorporationType = (item: TaishakuTaishoKmkType): boolean => {
      if (corporationType.value === corporationTypeType.Type1) {
        return !item.isRequiredSettingType1
      } else {
        return !item.isRequiredSettingType2To5
      }
    }

    return {
      loading,
      isSucceedRequest,
      requestError,
      hasError,

      ryudoShisanItems,
      yukeiKoteiShisanItems,
      mukeiKoteiShisanItems,
      otherShisanItems,
      ryudoFusaiItems,
      koteiFusaiItems,
      tsumitateKinItems,
      hyokaSagakuItems,

      shisanGokei,
      koteiShisanGokei,
      fusaiGokei,
      junshisanGokei,
      fusaiJunshisanGokei,

      shushikin,
      kikin,
      outputFundType,

      corporationName,
      location,
      medicalCorporationNumber,
      corporationType,
      accountingEndDate,

      onDismissAlert,
      onClickedRegister,
      onClickedRecalculate,
      onClickedExport,
      isDisabledByCorporationType
    }
  }
})
